import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Image, Item, Unit } from '../types'
import clsx from 'clsx'
import { ItemImages } from './item_images'
import { sortImagesByOrdinality } from '../lib/sort_images'

import '../stylesheets/ListingSingle.scss'

const ImageUploadModal = lazy(() => import('./image_upload_modal').then(module => ({ default: module.ImageUploadModal })))

const noop = () => {}

type Props = Readonly<{
  // cardCategoryVariant: CardCategoryVariant
  editing: boolean
  upload?: boolean
  saleChip?: boolean
  items: Item[]
  unit: Unit
  onImageUploaded?: (image: Image) => void
}>

const ListingSingle = ({ editing, upload = false, saleChip = false, items, unit, onImageUploaded }: Props) => {
  const { t, i18n } = useTranslation()

  const [images, setImages] = useState<Image[]>()
  const [userImages, setUserImages] = useState<boolean>(false)

  // determine category
  // const category = useMemo<Tag|undefined>(() => {
  //   if (item.tags.length === 0) return

  //   return item.tags.reduce((prev, current) => prev.depth > current.depth || current.type !== 'Category' ? prev : current)
  // }, [item.tags])

  const itemName = useMemo(() => {
    return items.map(item => {
      if (i18n.language === 'ja') {
        return item.nameJa
      }

      return item.nameEn
    }).join(' / ')
  }, [i18n.language, items])

  // const itemCategoryLink = useMemo(() => {
  //   if (!category) return

  //   let title: string|undefined = category.slug ? category.slug.split('_')[0] : undefined

  //   if (!title || title.length < 1) {
  //     title = (i18n.language === 'ja' && category.nameJa) ?? !category.nameEn ? category.nameJa : category.nameEn
  //   }

  //   return <Link to={ `/categories/${category.id}` } className="card-category"><h4>{ title }</h4></Link>
  // }, [category, i18n.language])

  useEffect(() => {
    if (unit.images && unit.images.length > 0) {
      setImages(unit.images)
      setUserImages(true)
      return
    }

    setImages(items.map(item => item.images[0]))
    setUserImages(false)
  }, [items, unit.images])

  const listing = useMemo(() => {
    if (!unit.listings) return null

    return unit.listings[0]
  }, [unit.listings])

  const holicListing = useMemo(() => {
    return unit.listings?.find(l => l.platform === 'holic' && !l.soldAt)
  }, [unit.listings])

  const showSaleChip = !!(saleChip && unit.public !== false && holicListing)

  const price = useMemo(() => {
    if (!listing) return

    return Math.round(listing.priceJpy)
  }, [listing])

  const saleChipUi = useMemo(() => {
    if (!showSaleChip || !holicListing) return

    const amount = Math.round(holicListing.priceJpy)
    const priceLabel = amount < 1
      ? t('listings.price.free')
      : t('listings.price.price_with_currency_symbol', { price: amount.toLocaleString() })

    return (
      <span className='sale-chip'>
        <span className='label'>{ t('cards.item.for_sale_on_holic') }</span>
        <span className='amount'>{ priceLabel }</span>
      </span>
    )
  }, [holicListing, showSaleChip, t])

  const priceUi = useMemo(() => {
    if (unit?.public === false) {
      return <span className='private'></span>
    }

    if (saleChipUi) return

    if (typeof price === 'undefined') return

    if (price < 1) {
      return <span className='price'><span className='number'>{ t('listings.price.free') }</span></span>
    }

    return <span className='price'><span className='number'>{ price.toLocaleString() }</span><span className='symbol'>{ t('listings.price.currency_symbol') }</span></span>
  }, [price, saleChipUi, t, unit?.public])

  const sold = useMemo(() => {
    return !!listing?.soldAt
  }, [listing])

  const unitLink = useMemo(() => {
    if (!unit?.userId || !unit.id) return ''

    const path = `/u/${unit.userId}/units/${unit.id}`
    return showSaleChip ? `${path}#buy` : path
  }, [showSaleChip, unit])

  const grade = useMemo(() => {
    if (listing && listing.platform !== 'holic') {
      return <span className={ clsx('platform', [listing.platform]) }>{ listing.platform }</span>
    }

    if (unit.condition === 0) {
      return <span className='grade none' title={ t(`collections.conditions.long.${unit.condition}`) }>
        -
      </span>
    }

    return <span className={ `grade grade-${unit.condition}` }>
      <span className='score'>{ t(`collections.conditions.short.${unit.condition}`) }</span>
    </span>
  }, [listing, t, unit.condition])

  const noImages = useMemo(() => {
    if (!listing || (images && images.length > 0)) return

    return <div className='no-images'>{ t('listings.no_images') }</div>
  }, [images, listing, t])

  const uploadImagesCallback = useCallback((image: Image) => {
    onImageUploaded?.(image)
  }, [onImageUploaded])

  const firstImageAsArray = useMemo(() => {
    if (!images || images.length === 0) return []

    const firstImage = sortImagesByOrdinality(images)[0]
    return firstImage ? [firstImage] : []
  }, [images])

  const listingContents = useMemo(() => {
    return <>
      { saleChipUi }
      { noImages }
      <ItemImages images={ firstImageAsArray } resolution='2x' navigationStyle='none' />
      <div className='bottom'>
        <div className='item-name'>
          <h3>{ itemName }</h3>
          { /* itemCategoryLink */ }
        </div>
        {
          upload
            ? <div className='unit-photo-upload'>
              <Suspense fallback={ null }>
                <ImageUploadModal unit={ unit } onClose={ noop } callback={ uploadImagesCallback } showCancelButton={ false } />
              </Suspense>
            </div>
            : null
        }
        <div className={ clsx('item-details', !showSaleChip && price ? `price-digits-${price.toString().length}` : '') }>
          { grade }
          { priceUi }
        </div>
      </div>
    </>
  }, [firstImageAsArray, grade, itemName, noImages, price, priceUi, saleChipUi, showSaleChip, unit, upload, uploadImagesCallback])

  const staticCard = editing || upload

  return (
    <li className={ clsx('listing', { userImages }, { private: unit?.public === false }, { 'for-sale': listing }, { sold }) } id={ `${unit.id ?? 0}` }>
      {
        staticCard
          ? <div>{ listingContents }</div>
          : <Link to={ unitLink }>
            { listingContents }
          </Link>
      }
    </li>
  )
}

export { ListingSingle }
